import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './AmazonSalesData.css';

const AmazonSalesData = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchProgress, setFetchProgress] = useState('');

  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const firstDayWeekday = firstDayOfMonth.getDay();

  // Generate calendar days
  const generateCalendarDays = () => {
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayWeekday; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateString = date.toISOString().split('T')[0];
      days.push({
        day,
        date: dateString,
        hasData: calendarData[dateString] || false
      });
    }
    
    return days;
  };

  // Fetch calendar data for the current month
  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth, currentYear]);

  const fetchCalendarData = async () => {
    try {
      const startOfMonth = new Date(currentYear, currentMonth, 1).toISOString().split('T')[0];
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0).toISOString().split('T')[0];
      
      console.log('Fetching calendar data for:', startOfMonth, 'to', endOfMonth);
      
      const response = await apiCall(`/api/amazon-sales/calendar-data?start_date=${startOfMonth}&end_date=${endOfMonth}`);
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Calendar data received:', data);
        setCalendarData(data);
      } else {
        const errorText = await response.text();
        console.error('API error:', errorText);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const handleFetchData = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }
    
    setIsLoading(true);
    setFetchProgress('🚀 Starting optimized data fetch with parallel processing... This should be much faster!');
    
    try {
      setFetchProgress('🔄 Connecting to Amazon API and fetching orders...');
      
      const response = await apiCall('/api/amazon-sales/fetch-data', {
        method: 'POST',
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate
        })
      });
      
      const data = await response.json();
      console.log('Fetch data response:', data);
      
      if (response.ok && data.success) {
        // Success case
        const successMessage = `✅ Data fetch completed successfully!\n\n` +
          `📊 Total orders found: ${data.total_orders_found}\n` +
          `✅ Orders processed: ${data.total_orders_processed}\n` +
          `📝 New records added: ${data.new_records_added}\n\n` +
          `All data has been successfully imported to the database.`;
        
        alert(successMessage);
        
        // Refresh calendar data to show the new data
        fetchCalendarData();
      } else {
        // Error case
        let errorMessage = '❌ Data fetch failed!\n\n';
        
        if (data.error) {
          errorMessage += data.error;
        } else {
          errorMessage += 'An unknown error occurred while fetching data.';
        }
        
        if (data.failed_orders && data.failed_orders.length > 0) {
          errorMessage += '\n\nFailed orders:\n';
          data.failed_orders.forEach(failed => {
            errorMessage += `• Order ${failed.order_id}: ${failed.error}\n`;
          });
        }
        
        if (data.total_orders_processed > 0) {
          errorMessage += `\n\nProgress: ${data.total_orders_processed}/${data.total_orders_found} orders processed before failure.`;
        }
        
        errorMessage += '\n\nNo data was saved to the database. Please try again later.';
        
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('❌ Network error while fetching data. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
      setFetchProgress('');
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="amazon-sales-data">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Amazon Sales Data Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Page allows manual download of Amazon Sales Data</li>
              <li>Be patient as this can take a long time</li>
              <li>Suggest downloading only one day a time</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="calendar-section">
        <div className="calendar-header">
          <button onClick={handlePreviousMonth} className="month-nav-btn">
            &lt; Previous
          </button>
          <h2>{monthNames[currentMonth]} {currentYear}</h2>
          <button onClick={handleNextMonth} className="month-nav-btn">
            Next &gt;
          </button>
        </div>

        <div className="calendar">
          <div className="calendar-weekdays">
            {weekdays.map(day => (
              <div key={day} className="weekday-header">{day}</div>
            ))}
          </div>
          
          <div className="calendar-days">
            {generateCalendarDays().map((dayData, index) => (
              <div 
                key={index} 
                className={`calendar-day ${!dayData ? 'empty' : ''} ${dayData?.hasData ? 'has-data' : ''}`}
              >
                {dayData && (
                  <>
                    <span className="day-number">{dayData.day}</span>
                    {dayData.hasData && <div className="data-indicator"></div>}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-indicator has-data"></div>
            <span>Days with more than 25 orders</span>
          </div>
        </div>
      </div>

      <div className="data-fetch-section">
        <h3>Fetch Data by Date Range</h3>
        <div style={{ 
          marginBottom: '15px', 
          padding: '10px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '4px',
          fontSize: '14px',
          color: '#856404'
        }}>
          <strong>⚠️ Important:</strong> This process requires 100% data capture reliability. 
          If any order fails to download after 10 retry attempts, the entire process will stop 
          and no data will be saved to the database. This ensures data integrity.
        </div>
        <div className="date-range-form">
          <div className="form-group">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="endDate">End Date:</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          
          <button 
            onClick={handleFetchData}
            disabled={isLoading || !startDate || !endDate}
            className="fetch-btn"
          >
            {isLoading ? 'Fetching...' : 'Fetch Data'}
          </button>
          
          {fetchProgress && (
            <div className="fetch-progress" style={{ 
              marginTop: '10px', 
              padding: '10px', 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #2196f3', 
              borderRadius: '4px',
              fontSize: '14px',
              color: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div className="spinner" style={{
                width: '20px',
                height: '20px',
                border: '2px solid #2196f3',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <span>{fetchProgress}</span>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default AmazonSalesData; 